/**
 * The single-person drawer: email + occupation (+ optional WorkOS role),
 * one submit. Presentational — the page owns the pipeline
 * (`useAddSiteUser`) and the toast. Shared by Workforce ("Add user") and
 * Members ("Invite user"), which is why the title, the submit label and
 * the org-admin option are props rather than baked in.
 *
 * #241: the role is the SITE role (Reader / Writer / Admin on this site).
 * The org-wide WorkOS role is a separate, explicitly labelled picker for org
 * admins: Orientation admin (ORIENTATION-ROLES P4) everywhere, Org admin only
 * where `allowOrgAdmin` is set. A Reader can get "Allow authoring" (P1),
 * offered to a site admin of the site.
 */
import { useState } from 'react';
import { DrawerShell } from '@/components/Workforce/DrawerShell';
import { EmailSuggestInput } from '@/components/Workforce/EmailSuggestInput';
import { OccupationSelect } from '@/components/OccupationSelect';
import { SiteRoleSelect } from '@/components/Workforce/SiteRoleSelect';
import type { SiteRole } from '@/lib/siteRole';
import { ALLOW_AUTHORING_HINT, showAllowAuthoring } from '@/lib/siteAccess';
import type { UserRow } from '@/hooks/useUsers';
import type { WorkosRosterState } from '@/hooks/useWorkosRoster';
import type { AddSiteUserInput } from '@/hooks/useAddSiteUser';

export function AddUserModal({
  title = 'Add user',
  submitLabel = 'Add',
  allowOrgAdmin = false,
  viewerIsOrgAdmin,
  viewerIsSiteAdmin,
  defaultProjectId,
  busy,
  onCancel,
  onSubmit,
  onSwitchToBulk,
  users,
  workosRoster,
}: {
  title?: string;
  submitLabel?: string;
  /** Members page: offer "also make org admin" (WorkOS `admin`), shown only
   *  when the viewer is an org admin themselves. */
  allowOrgAdmin?: boolean;
  /** Gates the site Admin choice and the org role picker. */
  viewerIsOrgAdmin: boolean;
  /** Site admin of this site: may set Allow authoring (D7). */
  viewerIsSiteAdmin: boolean;
  defaultProjectId: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: AddSiteUserInput) => void;
  onSwitchToBulk: () => void;
  users: UserRow[];
  workosRoster: WorkosRosterState;
}) {
  const [email, setEmail] = useState('');
  const [occupation, setOccupation] = useState('');
  const [siteRole, setSiteRole] = useState<SiteRole>('Reader');
  const [orgRole, setOrgRole] = useState<'member' | 'orientation-admin' | 'admin'>('member');
  const [allowAuthoring, setAllowAuthoring] = useState(false);
  const offerAuthoring = showAllowAuthoring(siteRole, viewerIsSiteAdmin);
  const projectId = defaultProjectId;
  const noSite = projectId === '';
  const canSubmit = email.trim() !== '' && !noSite && occupation !== '';
  const disabledReason = noSite
    ? 'Pick a site in the topbar first'
    : email.trim() === ''
      ? 'Email required'
      : occupation === ''
        ? 'Occupation required'
        : undefined;

  return (
    <DrawerShell
      title={title}
      busy={busy}
      canSubmit={canSubmit}
      disabledReason={disabledReason}
      submitLabel={submitLabel}
      onCancel={onCancel}
      onSubmit={() =>
        onSubmit({
          email: email.trim(),
          occupation: occupation.trim(),
          projectId,
          siteRole,
          roleSlug: viewerIsOrgAdmin && orgRole !== 'member' ? orgRole : undefined,
          allowAuthoring: offerAuthoring && allowAuthoring,
        })
      }
    >
      <EmailSuggestInput
        label="Email"
        value={email}
        onChange={setEmail}
        placeholder="jane@example.com"
        required
        users={users}
        workosRoster={workosRoster}
        currentProjectId={projectId}
      />
      <OccupationSelect
        value={occupation}
        onChange={setOccupation}
        label="Occupation *"
      />
      <SiteRoleSelect
        value={siteRole}
        onChange={setSiteRole}
        viewerIsOrgAdmin={viewerIsOrgAdmin}
        disabled={busy}
      />
      {offerAuthoring ? (
        <label title={ALLOW_AUTHORING_HINT} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12 }}>
          <input
            type="checkbox"
            checked={allowAuthoring}
            disabled={busy}
            onChange={(e) => setAllowAuthoring(e.target.checked)}
          />
          <span>
            <b>Allow authoring</b>: {ALLOW_AUTHORING_HINT}
          </span>
        </label>
      ) : null}
      {viewerIsOrgAdmin ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
          <span className="label-up">Org role</span>
          <select
            className="inp"
            value={orgRole}
            disabled={busy}
            onChange={(e) => setOrgRole(e.target.value as typeof orgRole)}
          >
            <option value="member">Member (site roles only)</option>
            <option value="orientation-admin">Orientation admin</option>
            {allowOrgAdmin ? <option value="admin">Org admin</option> : null}
          </select>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            {orgRole === 'orientation-admin'
              ? 'Builds orientations and manages people on every site. Cannot change site roles or invite. Applies to a new login only.'
              : orgRole === 'admin'
                ? 'Admin on every site in this organization. Applies to a new login only.'
                : 'Rights come from the site role above.'}
          </span>
        </label>
      ) : null}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
        Joins <b>{noSite ? '(no site)' : projectId}</b> as a <b>{siteRole}</b>,
        occupation <b>{occupation || '—'}</b>. Orientations are assigned on the{' '}
        <b>Orientation Assignment</b> page; any <code>auto_assign</code>{' '}
        orientation whose target matches this occupation will enroll the
        user automatically.
      </div>
      <div style={{ fontSize: 11, marginTop: 6 }}>
        Adding a whole shift?{' '}
        <button
          type="button"
          className="btn sm ghost"
          style={{ padding: '0 4px', fontSize: 11 }}
          disabled={busy}
          onClick={onSwitchToBulk}
        >
          Paste a list instead
        </button>
      </div>
    </DrawerShell>
  );
}
